import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Cart-page "Add something extra" popup (blocks/cart-addons.liquid): wine, chocolates, teddy
 * bear, foil balloons, and the simple finishing-touch toggles. Every option is its own
 * standalone Shopify product/variant added as a cart line carrying `_addon_line: 'true'`
 * (snippets/cart-products.liquid reads that to suppress the per-line "+ Add personalized
 * card" prompt under them).
 *
 * State model — the one source of truth is `#addonLineKeys`: a Map of
 *   variantId(string) -> that add-on line's cart `key`
 * for every `_addon_line` line currently in the cart. EVERY control is an independent
 * add/remove toggle against that map, with no grouping:
 *   - variant not in the map -> click ADDS it, then stores the returned line key
 *   - variant in the map     -> click REMOVES that exact line by its key
 * This applies identically to the on/off toggle cards (Ferrero, ribbon, …) AND to the
 * individual variant pills on wine / foil balloons / teddy bear, so e.g. Red wine and
 * White wine can both be active at once (both lines in the cart).
 *
 * Why the map is rebuilt from the real cart so often: this popup's markup lives inside
 * <cart-items-component>, so the cart-section morph that runs after every cart mutation
 * repaints the popup back to its static server markup (first tab active, every button
 * "Add"). morph preserves the open <dialog>, so the popup stays open — but its tab and
 * control states are wiped. So:
 *   - #activeTab / #addonLineKeys are held in JS and survive the morph (the element isn't
 *     replaced);
 *   - updatedCallback() (fired by morph, in a microtask, before the next paint) repaints
 *     tab + every control from that state — no visible flicker;
 *   - #reconcileFromServer() (GET /cart.js, filtered to `_addon_line` lines) re-derives the
 *     map on connect, whenever the dialog opens, and after every cart mutation, so the
 *     popup reflects real cart contents instead of firing blind.
 *
 * Also owns the "Add-on notes" field, written to cart.attributes merged with whatever's
 * already there (delivery-scheduler.js writes other keys to the same cart).
 */
class CartAddonsComponent extends Component {
  /** @type {Map<string, string>} variantId -> cart line key, for every `_addon_line` line in the cart */
  #addonLineKeys = new Map();

  /** @type {Set<string>} variantIds with an add/remove request in flight */
  #busyVariants = new Set();

  /** @type {string} the tab panel currently shown */
  #activeTab = 'drinks';

  /** @type {number} bumped per #reconcileFromServer call so a slow earlier fetch can't overwrite a newer one */
  #reconcileToken = 0;

  /** @type {number | undefined} */
  #reconcileTimer;

  /** @type {number | undefined} */
  #noteUpdateTimeout;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#activeTab =
      /** @type {HTMLElement | null} */ (this.querySelector('[role="tab"][aria-selected="true"]'))?.dataset.tab ??
      'drinks';

    // Any cart change — ours or from anywhere else — re-derives the map from the real cart.
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (event) => {
        const promise = /** @type {any} */ (event).promise;
        if (promise?.then) promise.then(() => this.#scheduleReconcile(), () => this.#scheduleReconcile());
        else this.#scheduleReconcile();
      },
      { signal }
    );

    // Fresh truth every time the popup opens (add-ons changed on the cart page then the
    // dialog reopened; or the first open after a full page load).
    this.closest('dialog-component')?.addEventListener('dialog:open', () => this.#scheduleReconcile(), { signal });

    // Warm the map in the background so the first open is already correct.
    this.#scheduleReconcile();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#reconcileTimer);
  }

  /**
   * morph fires this (microtask, pre-paint) after repainting the popup to its static server
   * markup. Re-apply the tab and every control state from JS so the wipe is invisible.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyActiveTab();
    this.#renderFromModel();
    this.#dbg('repainted after cart-section morph');
  }

  // ---- Tabs -----------------------------------------------------------------

  /** @param {MouseEvent & {target: HTMLElement}} event */
  selectTab(event) {
    const button = event.target.closest('[data-tab]');
    if (!(button instanceof HTMLElement)) return;
    this.#activeTab = button.dataset.tab ?? 'drinks';
    this.#applyActiveTab();
  }

  #applyActiveTab() {
    for (const tab of this.querySelectorAll('[role="tab"]')) {
      tab.setAttribute('aria-selected', String(/** @type {HTMLElement} */ (tab).dataset.tab === this.#activeTab));
    }
    for (const panel of this.querySelectorAll('[data-tab-panel]')) {
      /** @type {HTMLElement} */ (panel).hidden =
        /** @type {HTMLElement} */ (panel).dataset.tabPanel !== this.#activeTab;
    }
  }

  // ---- Add / remove -------------------------------------------------------

  /** Toggle-card buttons (Ferrero, Raffaello, butterflies, lillies, ribbon, crown, bouquet box). @param {MouseEvent & {target: HTMLElement}} event */
  toggleAddon(event) {
    const button = event.target.closest('[data-addon-variant-id]');
    if (button instanceof HTMLElement) this.#toggleVariant(button.dataset.addonVariantId ?? '');
  }

  /** Individual variant pills — wine colours, foil quantities, teddy sizes. @param {MouseEvent & {target: HTMLElement}} event */
  handleTierPillClick(event) {
    const pill = event.target.closest('[data-tier-variant-id]');
    if (pill instanceof HTMLElement) this.#toggleVariant(pill.dataset.tierVariantId ?? '');
  }

  /**
   * The single add/remove path for every control. Adds the variant as an `_addon_line`
   * when it isn't in the cart; otherwise removes that exact line by its stored key.
   * @param {string} variantId
   */
  async #toggleVariant(variantId) {
    if (!variantId || variantId === '0' || this.#busyVariants.has(variantId)) return;

    const wasInCart = this.#addonLineKeys.has(variantId);
    const priorKey = this.#addonLineKeys.get(variantId);

    // Optimistic: flip the map + repaint immediately, nudge the header badge.
    this.#busyVariants.add(variantId);
    if (wasInCart) this.#addonLineKeys.delete(variantId);
    else this.#addonLineKeys.set(variantId, '__pending__');
    this.#renderFromModel();
    this.#bumpCartIconOptimistically(wasInCart ? -1 : 1);

    const result = await this.#mutateCartVariant(wasInCart ? 'remove' : 'add', variantId, priorKey);

    if (!result.ok) {
      if (wasInCart && priorKey) this.#addonLineKeys.set(variantId, priorKey);
      else this.#addonLineKeys.delete(variantId);
      this.#bumpCartIconOptimistically(wasInCart ? 1 : -1);
    } else if (!wasInCart) {
      this.#addonLineKeys.set(variantId, result.key || '__pending__');
    }

    this.#busyVariants.delete(variantId);
    this.#renderFromModel();
    // The cartLinesUpdate we dispatched will also trigger #scheduleReconcile() via the
    // document listener — that GET /cart.js is the authoritative correction.

    this.#dbg(
      `toggle variant ${variantId}: ${wasInCart ? 'REMOVE' : 'ADD'} -> ${result.ok ? 'ok' : 'FAILED'}` +
        `${result.key ? ` key=${result.key}` : ''}`
    );
  }

  /** TEMP diagnostic — remove once popup behaviour is signed off. */
  #dbg(msg) {
    const added = Array.from(this.#addonLineKeys, ([v, k]) => `${v}${k && k !== '__pending__' ? `:${k.slice(0, 8)}` : ''}`);
    console.debug(`[cart-addons-dbg] ${msg} | tab=${this.#activeTab} | in-cart add-ons: [${added.join(', ') || 'none'}]`);
  }

  // ---- Rendering from the model -----------------------------------------

  #renderFromModel() {
    for (const control of this.querySelectorAll('[data-addon-variant-id], [data-tier-variant-id]')) {
      const el = /** @type {HTMLElement} */ (control);
      const variantId = el.dataset.tierVariantId || el.dataset.addonVariantId || '';
      const inCart = this.#addonLineKeys.has(variantId);
      const busy = this.#busyVariants.has(variantId);

      el.setAttribute('aria-pressed', String(inCart));
      el.toggleAttribute('aria-busy', busy);
      if (el instanceof HTMLButtonElement) el.disabled = busy;

      // Toggle cards swap their label; pills keep their colour/size label.
      if (el.dataset.addonKey) el.textContent = inCart ? 'Added' : 'Add';
    }

    for (const priced of this.querySelectorAll('.cart-addons__card-price[data-tier-priced]')) {
      this.#renderTierPrice(priced.closest('[data-tier-group]'));
    }
  }

  /**
   * A `[data-tier-priced]` card (foil balloons, teddy bear) shows the exact price when
   * exactly one of its pills is active, otherwise falls back to its "From KSh X" text
   * (`data-from-text`). Cards without `data-tier-priced` (wine) keep their static price.
   * @param {Element | null} card
   */
  #renderTierPrice(card) {
    if (!(card instanceof HTMLElement)) return;
    const priceEl = /** @type {HTMLElement | null} */ (
      card.querySelector('.cart-addons__card-price[data-tier-priced]')
    );
    if (!priceEl) return;

    const active = Array.from(card.querySelectorAll('[data-tier-variant-id]')).filter((p) =>
      this.#addonLineKeys.has(/** @type {HTMLElement} */ (p).dataset.tierVariantId ?? '')
    );

    if (active.length === 1) {
      const priceMinor = Number(/** @type {HTMLElement} */ (active[0]).dataset.tierPriceMinor);
      if (Number.isFinite(priceMinor) && priceMinor > 0) {
        priceEl.textContent = formatMoney(
          priceMinor,
          this.dataset.moneyFormat || '{{amount}}',
          this.dataset.currency || 'USD'
        );
        return;
      }
    }
    if (priceEl.dataset.fromText) priceEl.textContent = priceEl.dataset.fromText;
  }

  // ---- Server reconciliation ------------------------------------------

  /** Coalesce the reconcile calls that can arrive back-to-back (our mutation + its cartLinesUpdate + dialog:open). */
  #scheduleReconcile() {
    clearTimeout(this.#reconcileTimer);
    this.#reconcileTimer = /** @type {any} */ (setTimeout(() => this.#reconcileFromServer(), 120));
  }

  async #reconcileFromServer() {
    const token = ++this.#reconcileToken;
    try {
      const cart = await this.#fetchCart();
      if (token !== this.#reconcileToken) return; // a newer reconcile superseded this one

      /** @type {Map<string, string>} */
      const next = new Map();
      for (const item of cart.items ?? []) {
        if (item?.properties?.['_addon_line'] === 'true' && item.quantity > 0) {
          next.set(String(item.variant_id), item.key);
        }
      }
      // Don't let the server snapshot clobber a variant whose own request is still in flight.
      for (const vid of this.#busyVariants) {
        if (this.#addonLineKeys.has(vid)) next.set(vid, /** @type {string} */ (this.#addonLineKeys.get(vid)));
        else next.delete(vid);
      }

      this.#addonLineKeys = next;
      this.#renderFromModel();
      this.#dbg('reconciled from GET /cart.js');
    } catch (error) {
      console.warn('[cart-addons] cart reconcile failed:', error);
    }
  }

  // ---- Cart mutation --------------------------------------------------

  /**
   * @param {'add' | 'remove'} action
   * @param {string} variantId
   * @param {string} [lineKey] - for 'remove', the exact cart line key to drop
   * @returns {Promise<{ ok: boolean, key?: string }>}
   */
  async #mutateCartVariant(action, variantId, lineKey) {
    const isAdd = action === 'add';
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'product',
        lines: isAdd
          ? [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity: 1 }]
          : [{ id: lineKey || variantId, quantity: 0 }],
        promise: deferred.promise,
      })
    );

    try {
      const url = isAdd ? Theme.routes.cart_add_url : Theme.routes.cart_change_url;
      const body = isAdd
        ? { id: variantId, quantity: 1, properties: { _addon_line: 'true' }, sections: this.#getCartSectionIds() }
        : { id: lineKey || variantId, quantity: 0, sections: this.#getCartSectionIds() };

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();

      if (isAdd && data.status) {
        const error = new Error(data.message || 'Add to cart failed');
        deferred.reject(error);
        this.dispatchEvent(new CartErrorEvent({ error: error.message, code: 'INVALID' }));
        return { ok: false };
      }

      if (isAdd) {
        // `/cart/add.js` with a single `id` returns only the added line — no whole-cart
        // items/count. Resolve with `cart: null` (a non-null `cart` is run through
        // @shopify/events' normaliser, which does `cart.lines.map()` with no guard and
        // throws "Cannot read properties of undefined (reading 'map')" for a partial
        // object), and read the new count out of the returned section HTML.
        deferred.resolve({
          cart: null,
          detail: {
            source: 'cart-addons',
            itemCount: this.#itemCountFromSections(data.sections),
            sections: data.sections,
            didError: false,
          },
        });
        return { ok: true, key: data.key };
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          items: data.items,
          source: 'cart-addons',
          itemCount: data.item_count,
          sections: data.sections,
          didError: false,
        },
      });
      return { ok: true };
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {any} */ (error)?.message || `Network error trying to ${action} an item`,
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      return { ok: false };
    }
  }

  // ---- Add-on notes ------------------------------------------------------

  /**
   * Debounced write to cart.attributes['Add-on notes'] — reads the current cart first and
   * merges, since /cart/update.js replaces the whole attributes object rather than merging
   * (delivery-scheduler.js writes different attribute keys on this same cart).
   */
  handleNoteInput() {
    if (this.#noteUpdateTimeout) clearTimeout(this.#noteUpdateTimeout);
    this.#noteUpdateTimeout = /** @type {any} */ (setTimeout(() => this.#syncNote(), 600));
  }

  async #syncNote() {
    const { noteInput } = this.refs;
    if (!noteInput) return;

    try {
      const cart = await this.#fetchCart();
      await fetch(Theme.routes.cart_update_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: { ...cart.attributes, 'Add-on notes': noteInput.value },
        }),
      });
    } catch (error) {
      console.warn('[cart-addons] Failed to sync add-on notes:', error);
    }
  }

  // ---- Helpers ---------------------------------------------------------

  /**
   * Optimistically nudges the header cart bubble before the network call resolves. cart-icon's
   * own StandardEvents.cartLinesUpdate listener corrects this to the real server count as soon
   * as our dispatched event resolves (or, on failure, the call site above undoes it directly).
   * @param {number} delta
   */
  #bumpCartIconOptimistically(delta) {
    if (!delta) return;

    const cartIcon = /** @type {{ currentCartCount?: number, renderCartBubble?: (count: number, animate?: boolean) => void } | null} */ (
      document.querySelector('cart-icon')
    );
    if (!cartIcon || typeof cartIcon.currentCartCount !== 'number' || !cartIcon.renderCartBubble) return;

    cartIcon.renderCartBubble(Math.max(0, cartIcon.currentCartCount + delta));
  }

  /**
   * Section IDs for every `cart-items-component` on the page — passed as the `sections`
   * request param so add/change responses include pre-rendered HTML, letting
   * cart-items-component morph the cart line list and total in place instead of a slow full
   * section re-fetch.
   */
  #getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => !!id)
      .join(',');
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Reads the post-mutation cart line count straight out of the re-rendered cart section
   * HTML that `/cart/add.js` returns when a `sections` param is passed — the hidden
   * `[ref="cartItemCount"]` node snippets/cart-products.liquid renders. Lets the add path
   * skip a second `/cart.js` round trip just to learn the new count. Returns 0 if the node
   * can't be found (the caller still has the optimistic header-badge bump as a fallback).
   * @param {Record<string, string> | undefined} sections
   * @returns {number}
   */
  #itemCountFromSections(sections) {
    if (!sections) return 0;
    for (const html of Object.values(sections)) {
      const text = new DOMParser()
        .parseFromString(String(html), 'text/html')
        .querySelector('[ref="cartItemCount"]')
        ?.textContent?.trim();
      if (text) {
        const n = parseInt(text, 10);
        if (!Number.isNaN(n)) return n;
      }
    }
    return 0;
  }
}

if (!customElements.get('cart-addons-component')) {
  customElements.define('cart-addons-component', CartAddonsComponent);
}
